<template>
  <d-modal
    class="modal-big"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('complaints.popup.editComplaint') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form>
        <validation-observer ref="complaint-edit-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('complaints.popup.description') }}:
            </label>

            <d-textarea
              :value.prop="description"
              v-model="description" />
          </div>

          <div class="form-group">
            <label>
              {{ $t('complaints.popup.handler') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="workers"
                  v-model="handled_by_id"
                  :reduce="worker => worker.id"
                  :filterBy="filterByForWorkers"
                  :placeholder="$t('users.selectWorker')"
                  label="name"
                  :clearable="false">
                  <template slot="no-options">
                    {{ $t('users.noUsersMatchingPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>

              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>

            <div class="form-group">
              <h6 class="mt-4 mb-2">
                {{ $t('complaints.popup.complainedItems') }}:
              </h6>

              <div
                v-for="(item, index) of itemsWithoutTransport"
                :key="'product-item-' + index"
                class="product-item">
                <div class="product-item-wrapper d-flex">
                  <d-badge
                    v-if="(item.order_product_id && !qtyOrderData.products[item.order_product_id]) || (item.order_service_id && !qtyOrderData.services[item.order_service_id])"
                    theme="danger"
                    class="mr-2">
                    {{ $t('complaints.popup.notInOrder') }}
                  </d-badge>
                  <strong v-if="item.order_product_id">
                    <d-badge
                      theme="dark"
                      class="mr-2">
                      {{ $t('complaints.popup.product') }}
                    </d-badge>
                    {{ [item.product_code, item.product_name].filter(part => !!part).join(' - ') }}
                  </strong>
                  <strong v-else-if="item.order_service_id">
                    <d-badge
                      theme="dark"
                      class="mr-2">
                      {{ $t('complaints.popup.service') }}
                    </d-badge>
                    {{ [item.service_code, item.service_name].filter(part => !!part).join(' - ') }}
                  </strong>
                </div>

                <div
                  :key="'product-item-complaint-params-' + index"
                  class="product-item-complaint-params mt-2 mb-4">
                  <div
                    :key="'product-item-complaint-params-item-' + index"
                    class="d-flex">
                    <d-textarea
                      :value.prop="item.description"
                      v-model="item.description"
                      class="w-50"
                      :placeholder="$t('complaints.popup.enterComplaintDescription')" />
                    <validation-provider
                      :rules="itemValidationRules(item)"
                      :name="'product-item-complaint-quantity-' + index"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-input-group
                        :class="{
                          'is-invalid': errors.length
                        }">
                        <input
                          :class="{
                            'form-control': true,
                            'is-invalid': errors.length
                          }"
                          :invalid="errors.length ? true : null"
                          type="number"
                          v-model="item.quantity" />
                        <d-input-group-text slot="append">
                          {{ $t('ui.piecesAbbr') }}
                        </d-input-group-text>
                      </d-input-group>
                      <d-form-invalid-feedback>
                        {{ errors[0] }}
                      </d-form-invalid-feedback>
                    </validation-provider>
                    <validation-provider
                      rules="required"
                      :name="'product-item-complaint-status-' + index"
                      class="w-25 ml-2"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="item.status"
                        :state="errors.length ? 'invalid' : null"
                        :options="complaintStatusOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </div>

              <h6 class="mt-4 mb-2">
                {{ $t('complaints.popup.complainTransport') }}:
              </h6>

              <div class="d-flex w-100 mt-2">
                <d-checkbox
                  inline
                  v-model="transportIsComplained"
                  toggle
                  toggle-small
                  class="mt-1">
                </d-checkbox>

                <strong class="pt-1" style="white-space: nowrap;">
                  {{ $t('complaints.popup.complainTransport') }}
                </strong>

                <validation-provider
                  v-if="transportIsComplained"
                  rules="required"
                  class="ml-3 w-100"
                  name="tracking-number-is-complained"
                  v-slot="{ errors }">
                  <d-input-group
                    :class="{
                      'is-invalid': errors.length,
                      'w-100': true
                    }">
                    <d-input-group-text slot="prepend">
                      {{ $t('complaints.popup.trackingNumber') }}
                    </d-input-group-text>
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="text"
                      v-model="shipment_tracking_number" />
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>
            </div>
          </div>
        </validation-observer>
      </d-form>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('complaints.popup.editComplaint') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'complaint-edit-popup',
  props: {
    complaintID: {
      default: 0,
      type: Number
    }
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.workersDataLoaded && this.orderDataLoaded;
    },
    complaintStatusOptions () {
      return [{
        text: this.$t('complaints.statusNames.received'),
        value: 'RECEIVED'
      }, {
        text: this.$t('complaints.statusNames.negotiated'),
        value: 'NEGOTIATED'
      }, {
        text: this.$t('complaints.statusNames.accepted'),
        value: 'ACCEPTED'
      }, {
        text: this.$t('complaints.statusNames.rejected'),
        value: 'REJECTED'
      }];
    },
    itemsWithoutTransport () {
      return this.items.filter(item => !item.shipment_tracking_number);
    },
    qtyOrderData () {
      let output = {
        services: {},
        products: {}
      };

      if (!this.orderData) {
        return output;
      }

      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];
        output.products[product.id] = product.quantity;

        for (let j = 0; j < product.services.length; j++) {
          let service = product.services[j];
          output.services[service.id] = service.quantity;
        }
      }

      for (let i = 0; i < this.orderData.services.length; i++) {
        let service = this.orderData.services[i];
        output.services[service.id] = service.quantity;
      }

      return output;
    }
  },
  data () {
    return {
      order_id: null,
      description: '',
      handled_by_id: null,
      shipment_tracking_number: '',
      items: [],
      // helpers
      workers: [],
      orderData: null,
      transportIsComplained: false,
      // flags
      loadWorkersError: false,
      loadOrderDataError: false,
      loadedData: false,
      orderDataLoaded: false,
      workersDataLoaded: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/complaints/item/',
      instanceFields: ['order_id', 'handled_by_id', 'description', 'items'],
      loadedKey: 'loadedData',
      errorKey: 'loadError',
      overrideID: this.complaintID,
      successAction: () => {
        let shipment = this.items.filter(item => item.shipment_tracking_number);

        if (shipment.length) {
          this.shipment_tracking_number = shipment[0].shipment_tracking_number;
          this.transportIsComplained = true;
        } else {
          this.shipment_tracking_number = '';
          this.transportIsComplained = false;
        }

        this.loadAdditionalData();
      }
    });
  },
  methods: {
    filterByForWorkers (option, label, search) {
      return (option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    handleClose () {
      this.$bus.$emit('close-complaint-edit-popup');
    },
    validateForm () {
      FormUtils.validate(this.$refs['complaint-edit-form'], this.saveComplaint);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        errorKey: 'loadWorkersError',
        loadedKey: 'workersDataLoaded',
        params: {
          return: 'get',
          where: {
            type: ['admin', 'worker']
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/orders/item/' + this.order_id,
        outputKey: 'orderData',
        method: 'get',
        errorKey: 'loadOrderDataError',
        loadedKey: 'orderDataLoaded'
      });
    },
    prepareItemsToSend () {
      let items = [];

      for (let i = 0; i < this.items.length; i++) {
        let item = this.items[i];
        let newItem = {
          quantity: item.quantity,
          status: item.status,
          description: item.description
        };

        if (item.order_product_id) {
          newItem.order_product_id = item.order_product_id;
        }

        if (item.order_service_id) {
          newItem.order_service_id = item.order_service_id;
        }

        items.push(newItem);
      }

      if (this.transportIsComplained) {
        items.push({
          shipment_tracking_number: this.shipment_tracking_number
        });
      }

      return items;
    },
    saveComplaint () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/complaints/update/' + this.complaintID,
        formData: {
          order_id: this.order_id,
          handled_by_id: this.handled_by_id,
          description: this.description,
          items: this.prepareItemsToSend()
        },
        successTitle: this.$t('complaints.popup.editSuccessTitle'),
        successText: this.$t('complaints.popup.editSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('complaints.popup.editErrorText'),
        successAction: () => {
          this.$bus.$emit('complaints-refresh-list');
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    },
    itemValidationRules (item) {
      let rules = 'required|min_value:1';

      if (item.order_product_id && this.qtyOrderData.products[item.order_product_id]) {
        rules = 'required|min_value:1|max_value:' + this.qtyOrderData.products[item.order_product_id];
      } else if (item.order_service_id && this.qtyOrderData.services[item.order_service_id]) {
        rules = 'required|min_value:1|max_value:' + this.qtyOrderData.services[item.order_service_id];
      }

      return rules;
    }
  }
}
</script>

<style lang="scss" scoped>
.product-item-complaint-params,
.service-item-complaint-params,
.product-item-service-item-complaint-params {
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 5px;
  padding: 10px;
}

.overlay-loading {
  left: 0;
  top: 0;
}
</style>
